/* CMS 예제 모델 ↔ 앱 AssetMeta(+extras) 매핑 — 순수 함수. plan.md §6.3 / §7.1.
   정본 스키마: Huenit_LAB_2.0 lib/registry/code_model_registry.dart (AssetMeta).

   핵심: 앱은 display_name + extras['tag'] 만 소비하므로,
   - display_name = 기본언어 제목
   - extras.tag   = 대표 필터(태그) — 앱 갤러리 색 그룹핑 키
   풍부한 다국어/카테고리/필터는 extras 에 보존(후속 앱 업그레이드가 소비).

   동결 시그니처(S2~S4 변경 금지):
     toAssetMeta(cmsExample, {ownerEmail, ownerId, sha256, sizeBytes, defaultLang}) => AssetMeta
     fromAssetMeta(assetMeta) => cmsExample
     buildManifest(assetMetas) => { assets: AssetMeta[] }
   opts 의 cats/filters/uploadedAt 는 선택적 확장(라벨 보강·결정적 타임스탬프용). */
import { mlGet } from '../lib/ml.js';

const KIND_BLOCK = 'blocklyCode';
/* ★ 레거시 전용 — 발행에는 절대 쓰지 않는다.
   초기(Phase 7 대기) 파이썬 예제는 최상위 kind 를 'pythonCode' 로 발행했는데, 앱
   AssetKind enum 은 {blocklyCode, aiModel} 뿐이라 앱 파서가 그 항목을 통째로 skip
   (구버전은 throw)했다 = 앱에서 영원히 안 보임. 지금은 그림·툴패스(BE-0007)와 같은
   방식으로 최상위는 blocklyCode 로 내고 extras.code_kind='python' 으로 가른다.
   이 상수는 **기존 자산 역매핑(fromAssetMeta)** 에서만 남아 있다. */
const LEGACY_KIND_PYTHON = 'pythonCode';

/* ---------- 예제 종류(CMS kind) → 발행 스펙 · 단일 정본 (BE-0007) ----------
   ★ 최상위 kind(AssetKind)는 앱 enum {blocklyCode, aiModel} 만 허용된다. 값을 늘리면
     구버전 앱의 AssetKind.values.byName() 이 throw 해 공식 예제 목록 전체가 죽는다
     (실제 사고 이력 — code_model_registry.dart AssetVisibility 주석). 따라서
     그림·툴패스도 최상위는 blocklyCode 로 발행하고 extras.domain/payload 로 구분한다.
   · domain  : code | engrave | ai   (없으면 앱이 code 로 폴백)
   · payload : picture(그림 원본) | project(엔그레이브 .huenit JSON) — engrave 에서만 의미
   · kind    : 이 예제를 "쓸 수 있는 대상" 토큰 배열. 그림은 드로잉·레이저 공용이라 배열. */
export const ENGRAVE_KINDS = ['drawing', 'laser'];
export const SOURCE_EXTS = ['svg', 'dxf', 'png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp'];

export const EXAMPLE_SPECS = {
  block:    { assetKind: KIND_BLOCK,  domain: 'code',    payload: '',        codeKind: 'block',  kindTokens: ['block'] },
  python:   { assetKind: KIND_BLOCK,  domain: 'code',    payload: '',        codeKind: 'python', kindTokens: ['python'] },
  picture:  { assetKind: KIND_BLOCK,  domain: 'engrave', payload: 'picture', codeKind: '',       kindTokens: null },
  toolpath: { assetKind: KIND_BLOCK,  domain: 'engrave', payload: 'project', codeKind: '',       kindTokens: null },
};

/* 파일명 또는 확장자 → allowlist 확장자(소문자). 목록 밖이면 ''. */
export function normalizeSourceExt(nameOrExt) {
  const s = String(nameOrExt || '').toLowerCase();
  const dot = s.lastIndexOf('.');
  const ext = (dot >= 0 ? s.slice(dot + 1) : s).trim();
  return SOURCE_EXTS.includes(ext) ? ext : '';
}

/* extras.kind 관용 파싱 — 문자열/배열 모두 허용(앱 _kindTokens 와 동일 관용도). */
export function readKindTokens(raw) {
  const arr = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return arr.map((x) => String(x).trim().toLowerCase()).filter(Boolean);
}

/* 엔그레이브 대상 정규화 — allowlist 교집합, 표시 순서 고정, 빈 값은 payload 별 기본값.
   기본값: 그림=드로잉·레이저 공용, 툴패스=드로잉(앱 _engraveKinds 폴백과 동일 방향). */
function normalizeEngraveKinds(raw, payload) {
  const want = new Set(readKindTokens(raw));
  const picked = ENGRAVE_KINDS.filter((k) => want.has(k));
  if (picked.length) return picked;
  return payload === 'project' ? ['drawing'] : [...ENGRAVE_KINDS];
}

/* CMS example → AssetMeta (S3 meta.json) */
export function toAssetMeta(cmsExample, opts = {}) {
  const {
    ownerEmail, ownerId, sha256, sizeBytes,
    defaultLang = 'ko',
    uploadedAt,          // (선택) 결정적 타임스탬프 — 미지정 시 현재 시각
    cats, filters,       // (선택) 라벨 보강용 분류 목록
  } = opts;
  const ex = cmsExample || {};
  const spec = EXAMPLE_SPECS[ex.kind] || EXAMPLE_SPECS.block;
  const title = ex.title || {};
  const desc = ex.desc || {};
  const filterIds = Array.isArray(ex.filterIds) ? ex.filterIds : [];

  // 대표 태그(extras.tag) — 앱 갤러리 현재 필터 키. filters 목록이 있으면 라벨로,
  // 없으면 첫 filter id 로 채운다(앱은 문자열이면 무엇이든 색 그룹핑에 사용).
  const repId = filterIds[0];
  let repTag = repId || '';
  let repTagI18n = null;                       // 대표 태그의 다국어 라벨(extras.tag_i18n)
  if (repId && Array.isArray(filters)) {
    const f = filters.find(x => x.id === repId);
    if (f) { repTag = mlGet(f.label, defaultLang) || repId; repTagI18n = f.label; }
  }

  // 카테고리 라벨(extras.category_label) — 조회 편의. cats 목록 있으면 채움.
  let categoryLabel;
  if (ex.catId && Array.isArray(cats)) {
    const c = cats.find(x => x.id === ex.catId);
    if (c) categoryLabel = c.label;
  }

  const extras = {
    i18n: { title, desc },
    category: ex.catId || '',
    tags: filterIds,
  };
  // 종류 계약(BE-0007). code_kind 는 현행 앱이 읽는 키라 코드 예제에 계속 실어 준다
  // (신규 UI 는 extras.kind 를 읽으므로 둘 다 발행해야 양쪽이 동작한다).
  if (spec.codeKind) extras.code_kind = spec.codeKind;
  extras.domain = spec.domain;
  extras.kind = spec.kindTokens || normalizeEngraveKinds(ex.engraveKinds, spec.payload);
  if (spec.payload) extras.payload = spec.payload;
  if (spec.payload === 'picture') {
    // 그림 원본 확장자 — 앱이 다운로드 파일의 논리 이름(XFile)을 복원하는 데 쓴다.
    const se = normalizeSourceExt(ex.sourceExt || ex.file);
    if (se) extras.source_ext = se;
  }
  if (repTag) extras.tag = repTag;
  if (repTagI18n) extras.tag_i18n = repTagI18n;   // 다국어 대표 태그(앱 EN 모드 등)
  // 전 태그 다국어 라벨 맵 {tagId:{ko,en,..}} — 앱 다중 태그 필터 칩이 admin 라벨과
  // 일치하도록(26.07.03-admin §2 / R-0746 tags_i18n 소비. 맵 형태 관용 파싱 확인).
  if (filterIds.length && Array.isArray(filters)) {
    const tagsI18n = {};
    for (const fid of filterIds) {
      const f = filters.find(x => x.id === fid);
      if (f && f.label) tagsI18n[fid] = f.label;
    }
    if (Object.keys(tagsI18n).length) extras.tags_i18n = tagsI18n;
  }
  if (categoryLabel) extras.category_label = categoryLabel;
  if (ex.file) extras.file_name = ex.file;   // 라운드트립 보존(원본 파일명; S3 객체키는 고정)
  // 선택적 썸네일 — 절대 HTTPS URL(AI 모델 thumbnail 관례와 동일). 앱은 후속 라운드에서 소비.
  if (ex.thumbnail) extras.thumbnail = ex.thumbnail;
  // 배치 순서 — 묶음(order_group)+예제(order_index). 앱은 manifest 배열 순서로 소비(+후속 라운드 명시 정렬).
  if (ex.orderGroup != null && ex.orderGroup !== '' && ex.orderIndex != null && ex.orderIndex !== '') {
    extras.order_group = Number(ex.orderGroup);
    extras.order_index = Number(ex.orderIndex);
  }

  const meta = {
    id: ex.id,
    kind: spec.assetKind,
    display_name: mlGet(title, defaultLang),
    ownership: 'official',
    visibility: 'public',
    size_bytes: sizeBytes ?? 0,
    sha256: sha256 ?? '',
    uploaded_at: uploadedAt || new Date().toISOString(),
    extras,
  };
  const description = mlGet(desc, defaultLang);
  if (description) meta.description = description;
  if (ownerEmail != null) meta.owner_email = ownerEmail;
  if (ownerId != null) meta.owner_id = ownerId;
  return meta;
}

/* AssetMeta → CMS example (편집 화면 복원용) */
export function fromAssetMeta(assetMeta) {
  const m = assetMeta || {};
  const extras = m.extras || {};
  // 레거시 자산(kind:'pythonCode')도 파이썬으로 복원 — 재발행 전 목록/편집이 정상 동작.
  const isPython = extras.code_kind === 'python' || m.kind === LEGACY_KIND_PYTHON;
  const i18n = extras.i18n || {};
  const title = i18n.title || (m.display_name ? { ko: m.display_name } : {});
  const desc = i18n.desc || (m.description ? { ko: m.description } : {});
  // 종류 역매핑 — domain 이 없으면 code(기존 자산 회귀 없음). engrave 는 payload 로 가른다.
  const isEngrave = extras.domain === 'engrave';
  const kind = isEngrave
    ? (extras.payload === 'project' ? 'toolpath' : 'picture')
    : (isPython ? 'python' : 'block');
  return {
    id: m.id,
    kind,
    engraveKinds: isEngrave ? normalizeEngraveKinds(extras.kind, extras.payload) : [],
    sourceExt: extras.source_ext || '',
    file: extras.file_name || '',
    thumbnail: extras.thumbnail || '',
    orderGroup: extras.order_group != null ? extras.order_group : '',
    orderIndex: extras.order_index != null ? extras.order_index : '',
    catId: extras.category || '',
    filterIds: Array.isArray(extras.tags) ? extras.tags : [],
    title,
    desc,
  };
}

/* AssetMeta[] → official/manifest.json 본문 */
export function buildManifest(assetMetas) {
  return { assets: Array.isArray(assetMetas) ? assetMetas : [] };
}
